import { useRef, useState } from 'react';
import { useNow } from '../ui/useNow';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { timeInTz, minutesInTz } from '../../lib/format';
import { StatusBadge } from '../ui/Badge';
import { Button, bigButton } from '../ui/Button';
import { drawnAs, mutationState, refreshUntilShown } from '../ui/QueryStates';
import { CHECKING_LABEL, useQueueRead, useUnsentWrite } from '../ui/useConnection';
import { VISIT_STATUS, display, windowLabel } from '../status';
import { IconChevronRight } from '../icons';
import {
  countdown,
  countdownLabel,
  canNudge,
  nudgedStart,
  windowNotice,
  NUDGE_MINUTES,
} from './nextStop';

/**
 * The next stop — countdown, arrival window, address, and what to do about it (117, tech
 * half; Purrweb ref via `assets/115/DECISIONS.md` §7).
 *
 * 🔴 **`TechTodayPage` had NEITHER today.** It is one flat sorted list whose only
 * segmentation is a day pager and an "N of M done" count: no countdown, and no window at
 * all. `visits.window` is a `CHECK`-constrained column that travels in `boardColumns` to
 * every screen in the product and was rendered by exactly one of them (`FleetRoutes`, the
 * OFFICE's map) — so the arrival window the office promised the customer was visible to
 * dispatch and not to the person expected to keep it.
 *
 * ⭐ **Everything here comes off the query the page already runs.** `visit.myDay` selects
 * `visit: visits` — the whole row — plus the full address and the customer's phone, so the
 * card added no procedure, no field and no round trip.
 */
export function NextStopCard({
  row,
  startsAt,
  tz,
}: {
  row: NextStopRow;
  startsAt: number | null;
  tz: string;
}) {
  const { visit, job, customer, property } = row;

  /**
   * The tick.
   *
   * ⚠️ **30 seconds, for a display that reads in whole minutes.** A 60s interval drifts
   * against the minute it is rendering — the label would sit on "in 5m" for anywhere up to
   * two minutes depending on when the component mounted. Sampling twice per minute keeps
   * the printed number within half a minute of true without a per-second re-render in a
   * van on a battery.
   *
   * ⚠️ `now` is the DEVICE's clock, and nothing here can correct a phone that is set wrong
   * — see the module docblock in `nextStop.ts`. The scheduled time is printed beside the
   * countdown so a wrong one is catchable.
   */
  const now = useNow(30_000);

  const trpc = useTRPC();
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries();
  const enRoute = useMutation(trpc.visit.enRoute.mutationOptions({ onSuccess: refresh }));
  const move = useMutation(trpc.visit.reschedule.mutationOptions({ onSuccess: refresh }));
  // Settles once the day is read again, so the stop never reads En route with this live.
  const abort = useMutation(
    trpc.visit.abort.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, trpc.visit.myDay.pathFilter()),
    }),
  );

  const [nudging, setNudging] = useState(false);
  /**
   * 🔴 The stop as drawn (`drawnAs`) when "Not leaving yet" was tapped: ONE abort per tap,
   * however fast the second tap comes.
   *
   * `disabled` cannot stop a double tap: both taps land in the same render's handler before
   * the pending write re-renders the button, and each would mint its own key, which the
   * server cannot dedupe (TechVisitPage's `stateTappedAt`, the same lock).
   *
   * Released only when the abort is REFUSED, or its key can't be minted. One that lands
   * changes the row, so the lock stops matching once the day is read again; if that read
   * never comes, a tap on the stale En route card is ignored, because the server already has
   * the abort. A status key would have killed the control for good: a stop set back and put
   * en route again reads `en_route` both times.
   */
  const notLeavingAt = useRef<string | null>(null);

  const c = countdown(startsAt, now);
  const label = countdownLabel(c);
  const window = windowLabel(visit.window);
  /**
   * ⚠️ Minutes into the day at the SHOP, not on this phone. A van working a state line would
   * otherwise cross noon an hour early or late and be told its morning promise was broken
   * when it was not — `minutesInTz` is the same helper the dispatch timeline reads.
   */
  const notice = windowNotice(
    visit.window,
    visit.scheduledStartAt ? minutesInTz(visit.scheduledStartAt, tz) : null,
  );
  const s = display(VISIT_STATUS, visit.status);
  const address =
    [property.address1, property.city, property.state].filter(Boolean).join(', ') || null;
  const mapsHref = address
    ? `https://maps.google.com/?q=${encodeURIComponent(address)}`
    : null;

  /**
   * ⚠️ The same offline vocabulary the visit screen uses, not a second one. A tech taps
   * "On my way" from whichever screen is in front of them, and being told "Saved — sends
   * when you're back" on one and nothing on the other would make the two disagree about
   * whether the tap landed.
   */
  const enRouteState = mutationState(enRoute);
  const moveState = mutationState(move);
  const abortState = mutationState(abort);
  const queuedLabel = 'Saved — sends when you’re back';
  // Every hook runs on every render, then they combine (a `||` between hooks skips the later
  // ones and crashes the card when an earlier one turns true).
  const enRouteUnsent = useUnsentWrite('visit.enRoute', visit.id, 'visitId');
  const rescheduleUnsent = useUnsentWrite('visit.reschedule', visit.id, 'visitId');
  const abortUnsent = useUnsentWrite('visit.abort', visit.id, 'visitId');
  const unsent = enRouteUnsent || rescheduleUnsent || abortUnsent;
  // Locked while this device's saved changes have not been read (see TechVisitPage).
  const checking = !useQueueRead();
  const busy = enRoute.isPending || move.isPending || abort.isPending;

  /** The lock is taken before the key is minted, so a second tap never gets one. */
  const notLeavingYet = () => {
    const at = drawnAs(visit);
    if (notLeavingAt.current === at) return; // a second tap, on the stop as already tapped
    notLeavingAt.current = at;
    try {
      abort.mutate(
        { visitId: visit.id, ...idem() },
        { onError: () => void (notLeavingAt.current = null) },
      );
    } catch (err) {
      // Minting the key can throw before any mutation exists, and then nothing would ever
      // settle: release the lock so the button is not dead.
      notLeavingAt.current = null;
      throw err;
    }
  };

  /**
   * ⚠️ Both halves must hold: the SERVER refuses to move anything but a `scheduled` visit
   * (`whyNotReschedulable`, asked through `canNudge`), and there must be a start to measure
   * a nudge from. A stop with no `scheduledStartAt` gets the rest of the card and no move
   * control, rather than a button that would have to invent the instant it sends.
   */
  const movable = canNudge(visit.status) && startsAt !== null;

  return (
    <section
      className="mx-4 mb-3 rounded-[var(--radius)] border border-brand bg-surface p-4"
      aria-labelledby="next-stop-heading"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2
          id="next-stop-heading"
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Next stop
        </h2>
        <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
      </div>

      {/*
        The countdown, and the clock it is derived from, on one line.

        ⚠️ `aria-live="polite"` so a screen-reader user is told when the number changes —
        but on the SPAN that holds it, never on the section: announcing the address and the
        customer's name every thirty seconds would make the card unusable.
      */}
      <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
        {label ? (
          <span
            aria-live="polite"
            className={`font-heading text-3xl font-bold tabular-nums ${
              c.state === 'late' ? 'text-danger' : ''
            }`}
          >
            {label}
          </span>
        ) : null}
        <span className="text-sm text-muted-foreground">
          {visit.scheduledStartAt ? timeInTz(visit.scheduledStartAt, tz) : 'No time set'}
          {/*
            An arrival WINDOW is not a promise of a time, so it is named beside it.

            🔴 …unless the time and the promise now CONTRADICT each other, which this card's
            own "Running behind" can cause: +1h on an 11:30 morning call lands at 12:30 and
            this line used to read "12:30 PM · Morning". The notice REPLACES the label rather
            than sitting next to it — printing "Morning" and "Morning window missed" together
            is the same contradiction with more words.
          */}
          {notice ? '' : window ? ` · ${window}` : ''}
        </span>
        {notice ? (
          <span className="text-sm font-semibold text-danger">{notice}</span>
        ) : null}
      </p>

      <Link
        to="/tech/visits/$visitId"
        params={{ visitId: visit.id }}
        className="mt-3 flex items-start gap-2"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{customer.displayName}</span>
          <span className="block truncate text-sm text-muted-foreground">
            {job.title}
          </span>
          <span className="block text-sm text-muted-foreground">{address ?? '—'}</span>
        </span>
        <IconChevronRight size={16} className="mt-1 shrink-0 text-muted-foreground" />
      </Link>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {/*
          ⚠️ "On my way" is shown ONLY while the stop is `scheduled` — the one transition
          the machine allows from here. Once the tech is en route the button would be a
          control whose only outcome is a refusal, so the slot goes to Directions alone.
        */}
        {visit.status === 'scheduled' ? (
          <Button
            variant="ghost"
            className={bigButton}
            disabled={busy || unsent}
            onClick={() => enRoute.mutate({ visitId: visit.id, ...idem() })}
          >
            {enRouteState === 'queued'
              ? queuedLabel
              : checking
                ? CHECKING_LABEL
                : 'On my way'}
          </Button>
        ) : null}
        <a
          href={mapsHref ?? undefined}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!mapsHref}
          className={`${bigButton} border border-border bg-surface ${
            mapsHref ? '' : 'pointer-events-none opacity-50'
          } ${visit.status === 'scheduled' ? '' : 'col-span-2'}`}
        >
          Directions
        </a>
      </div>

      {/*
        "Not leaving yet" (ops insights Phase 3, Q2): en route is set at check-out when the
        next stop starts within the hour, and a tech who isn't leaving yet (parts run, lunch)
        sets it back here, before the customer's link says "On the way" too early. It sends
        `visit.abort` (en_route → scheduled), the write the visit page's "I had to turn back"
        sends, and "On my way" comes back once the day is read again after it lands. Quiet,
        in the slot "Running behind?" takes for a scheduled stop.
      */}
      {visit.status === 'en_route' ? (
        <Button
          variant="ghost"
          className="mt-2 min-h-11 w-full"
          disabled={busy || unsent}
          onClick={notLeavingYet}
        >
          {abortState === 'queued'
            ? queuedLabel
            : checking
              ? CHECKING_LABEL
              : 'Not leaving yet'}
        </Button>
      ) : null}

      {/*
        "Move this slot", as a two-tap disclosure rather than three live buttons.

        🔴 A single tap here changes a time a customer was PROMISED. Three bare nudge
        buttons sitting under a thumb in a moving van is a mis-tap that re-books somebody's
        afternoon with no way to tell it happened — so the deltas stay behind a disclosure,
        and the row that reveals them says what it is going to do.

        ⚠️ The nudge is measured from the SCHEDULED start, not from now — see `nudgedStart`.
      */}
      {movable ? (
        <div className="mt-2">
          {nudging ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Push this stop back. The office sees the new time.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {NUDGE_MINUTES.map((m) => (
                  <Button
                    key={m}
                    variant="ghost"
                    className="min-h-11 w-full"
                    disabled={busy || unsent}
                    onClick={() => {
                      const at = nudgedStart(startsAt, m);
                      if (!at) return;
                      setNudging(false);
                      move.mutate({
                        visitId: visit.id,
                        scheduledStartAt: at,
                        ...idem(),
                      });
                    }}
                  >
                    +{m < 60 ? `${m}m` : `${m / 60}h`}
                  </Button>
                ))}
              </div>
              <Button
                variant="ghost"
                className="min-h-11 w-full"
                onClick={() => setNudging(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              className="min-h-11 w-full"
              disabled={busy || unsent}
              onClick={() => setNudging(true)}
            >
              {moveState === 'queued'
                ? queuedLabel
                : checking
                  ? CHECKING_LABEL
                  : 'Running behind?'}
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}

/** What the card reads off a `visit.myDay` row — a subset, named so the page can pass one. */
export interface NextStopRow {
  visit: {
    id: string;
    status: string;
    scheduledStartAt: string | Date | null;
    window: string;
    updatedAt: string | Date;
  };
  job: { title: string };
  customer: { displayName: string };
  property: { address1: string | null; city: string | null; state?: string | null };
}
